/* eslint-disable clubmed/no-fake-component */
import { classnames } from 'Utils/classnames/classnames.utils';
import React, { Fragment } from 'react';
import { constants } from '@clubmed/utils';
import { ICONS } from 'Components/utils/icon/icons';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { getListValue, t } from 'Src/i18n';
import { Icon } from 'Components/frameworks/icon/Icon';
import { Spinner } from 'Components/frameworks/spinner/Spinner';
const { NO_TRANSPORT_VALUE } = constants;
export type Props = {
  changeDepartureCity: (arg0: string, toggleCriteriaExpansion?: () => void) => void;
  className?: string;
  dataTestId?: string;
  departureCity: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  optionsDepartureCities: DepartureCities;
  toggleCriteriaExpansion?: () => void;
  validateDepartureCity: (newDepartureCity?: string) => void;
  withNoTransport?: boolean;
  shouldShowNoTransportButtonMobile?: boolean;
};
export const FilterTransport = ({
  changeDepartureCity,
  className,
  dataTestId = 'FilterTransport',
  departureCity,
  isDisabled,
  isLoading,
  optionsDepartureCities,
  toggleCriteriaExpansion,
  validateDepartureCity,
  withNoTransport,
  shouldShowNoTransportButtonMobile = true,
}: Props) => {
  const { isDesktop } = useResponsive();
  const value = departureCity || '';
  const loading = isLoading || optionsDepartureCities.length === 0;
  const otherDepartureCities = withNoTransport
    ? optionsDepartureCities
    : optionsDepartureCities.filter(
        ({ value: departureCityCode }) => departureCityCode !== NO_TRANSPORT_VALUE,
      );
  const mainCities = getListValue('faceting.transport.departureCities.mainCitiesList') || [];
  const optionsMainDepartureCities = optionsDepartureCities.filter(({ value: cityCode }) =>
    mainCities.includes(cityCode),
  );

  const renderTitle = (shouldShowNoTransportButton) => {
    if (!isDesktop) {
      return (
        <div className="flex flex-col">
          {shouldShowNoTransportButton && (
            <div
              className={classnames('px-20 py-15', {
                'font-bold text-success': value === NO_TRANSPORT_VALUE,
              })}
            >
              <label
                className="flex cursor-pointer items-baseline justify-between rounded-2 hocus:bg-lightGrey"
                htmlFor={NO_TRANSPORT_VALUE}
              >
                <input
                  checked={value === NO_TRANSPORT_VALUE}
                  className="hidden"
                  data-testid={`${dataTestId}-${NO_TRANSPORT_VALUE}`}
                  disabled={isDisabled}
                  id={NO_TRANSPORT_VALUE}
                  name="TransportSelectFilter"
                  onChange={(e) =>
                    changeDepartureCity(
                      (e.target as HTMLInputElement).value,
                      toggleCriteriaExpansion,
                    )
                  }
                  onClick={(e) => validateDepartureCity((e.target as HTMLInputElement).value)}
                  type="radio"
                  value={NO_TRANSPORT_VALUE}
                />
                {t('faceting.transport.noTransportNeededMobile')}
                {value === NO_TRANSPORT_VALUE && <Icon name={ICONS.CHECK} width="0.625rem" />}
              </label>
            </div>
          )}
          <div className="border-b-1 border-lightGrey p-20 py-15">
            <p className="text-16 font-bold">{t('faceting.transport.transportNeededMobile')}</p>
          </div>
        </div>
      );
    }

    return (
      <div className="border-b-1 border-lightGrey p-20 py-15">
        <p className="text-16 font-bold">{t('faceting.when.from')}</p>
      </div>
    );
  };

  const renderDepartureCitiesOptions = (departureCities) =>
    departureCities.map((o) => (
      <li
        className={classnames({
          'font-bold text-success': o.value === value,
        })}
        key={o.value}
        data-testid="filterTransport-city"
      >
        <label
          className="-mx-20 flex cursor-pointer items-baseline justify-between rounded-2 px-20 py-5 hocus:bg-lightGrey"
          htmlFor={o.value}
        >
          <input
            checked={o.value === value}
            className="hidden"
            data-testid={`${dataTestId}-${o.value}`}
            disabled={o.isDisabled || isDisabled}
            id={o.value}
            name="TransportSelectFilter"
            onChange={(e) => changeDepartureCity(e.target.value, toggleCriteriaExpansion)}
            type="radio"
            value={o.value}
          />
          {o.label}
          {o.value === value && <Icon name={ICONS.CHECK} width="0.625rem" />}
        </label>
      </li>
    ));

  return (
    <div className={classnames('flex flex-col', className)} data-testid="FilterTransport">
      {renderTitle(shouldShowNoTransportButtonMobile)}
      {loading ? (
        <div className="flex flex-1 items-center justify-center p-20 leading-0">
          <Spinner size="small" />
        </div>
      ) : (
        <div className="max-h-none overflow-y-auto px-20 text-14 lg:max-h-240">
          {optionsMainDepartureCities.length > 0 && (
            <Fragment>
              <p className="border-b-1 border-dashed border-grey py-5 text-mediumGrey">
                {t('faceting.transport.departureCities.mainCities')}
              </p>
              <ul className="mb-10 flex-1 pb-5 lg:flex-none">
                {renderDepartureCitiesOptions(optionsMainDepartureCities)}
              </ul>
              <p className="border-b-1 border-dashed border-grey py-5 text-mediumGrey">
                {t('faceting.transport.departureCities.allCities')}
              </p>
            </Fragment>
          )}
          <ul className="flex-1 lg:flex-none">
            {renderDepartureCitiesOptions(otherDepartureCities)}
          </ul>
        </div>
      )}
    </div>
  );
};
